import { DEFAULT_CONFIG } from "./config.ts";
import { DECK_MANIFEST } from "./data/cards.ts";
import { RULES_DATA } from "./data/rules.ts";
import { AudioService } from "./services/audio.ts";
import { ScreenService } from "./services/screen.ts";
import { StorageService } from "./services/storage.ts";
import { ToastComponent } from "./ui/toast.ts";
import { GameEngine } from "./engine/game-engine.ts";
import type { GameStateSnapshot } from "./types.ts";

// Initialise Core Services
const audio = new AudioService();
const screen = new ScreenService();
const storage = new StorageService();
const toast = new ToastComponent();
const engine = new GameEngine(DEFAULT_CONFIG, DECK_MANIFEST, audio, screen, storage);

// DOM Elements
const viewHome = document.getElementById("view-home") as HTMLElement;
const viewCountdown = document.getElementById("view-countdown") as HTMLElement;
const viewGame = document.getElementById("view-game") as HTMLElement;

const modalRules = document.getElementById("modal-rules") as HTMLElement;
const modalPreorder = document.getElementById("modal-preorder") as HTMLElement;

const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const btnRules = document.getElementById("btn-rules") as HTMLButtonElement;
const btnCloseRules = document.getElementById("btn-close-rules") as HTMLButtonElement;
const btnPreorderHook = document.getElementById("btn-preorder-hook") as HTMLElement;
const btnClosePreorder = document.getElementById("btn-close-preorder") as HTMLButtonElement;
const btnSubmitPreorder = document.getElementById("btn-submit-preorder") as HTMLButtonElement;
const btnExitGame = document.getElementById("btn-exit-game") as HTMLButtonElement;

const inputEmail = document.getElementById("preorder-email") as HTMLInputElement;
const categoryPillsContainer = document.getElementById("category-pills") as HTMLElement;
const cardsCounter = document.getElementById("cards-counter") as HTMLElement;
const countdownDisplay = document.getElementById("countdown-display") as HTMLElement;
const gameCategoryBadge = document.getElementById("game-category-badge") as HTMLElement;
const gameCardText = document.getElementById("game-card-text") as HTMLElement;
const gameWatermark = document.getElementById("game-watermark") as HTMLElement;
const rulesListContainer = document.getElementById("rules-list") as HTMLElement;
const progressFill = document.getElementById("long-press-progress") as HTMLElement;

// Set static watermark from configuration
if (gameWatermark) {
  gameWatermark.textContent = DEFAULT_CONFIG.watermarkText;
}

// Render Rules
function renderRules(): void {
  rulesListContainer.innerHTML = "";
  for (const rule of RULES_DATA) {
    const li = document.createElement("li");
    li.className = "rule-item";

    const numSpan = document.createElement("span");
    numSpan.className = "rule-num";
    numSpan.textContent = String(rule.id);

    const bodyDiv = document.createElement("div");
    const strong = document.createElement("strong");
    strong.textContent = `${rule.title}: `;
    bodyDiv.appendChild(strong);
    bodyDiv.appendChild(document.createTextNode(rule.description));

    li.appendChild(numSpan);
    li.appendChild(bodyDiv);
    rulesListContainer.appendChild(li);
  }
}
renderRules();

// Render Categories
function renderCategories(activeCategories: ReadonlySet<string>): void {
  categoryPillsContainer.innerHTML = "";
  for (const cat of DECK_MANIFEST.categories) {
    const pill = document.createElement("button");
    pill.type = "button";
    pill.className = `category-pill ${activeCategories.has(cat) ? "selected" : ""}`;
    pill.textContent = cat;

    pill.addEventListener("click", () => {
      const toggled = engine.toggleCategory(cat);
      if (!toggled) {
        pill.animate(
          [{ transform: "scale(1)" }, { transform: "scale(1.12)" }, { transform: "scale(1)" }],
          { duration: 200 }
        );
      }
    });

    categoryPillsContainer.appendChild(pill);
  }
}

// Subscribe to State Changes
engine.subscribe((snapshot: GameStateSnapshot) => {
  // Update view activations
  viewHome.classList.toggle("active", snapshot.currentState === "IDLE");
  viewCountdown.classList.toggle("active", snapshot.currentState === "COUNTDOWN");
  viewGame.classList.toggle("active", snapshot.currentState === "ACTIVE_CARD");

  // Update modal visibilities
  modalRules.classList.toggle("open", snapshot.currentState === "RULES_MODAL");
  modalPreorder.classList.toggle("open", snapshot.currentState === "INTEREST_MODAL");

  // Update counter & categories
  cardsCounter.textContent = `${snapshot.totalAvailableCards} CARDS`;
  renderCategories(snapshot.activeCategories);

  // Update card content with smooth GPU transform
  if (snapshot.currentCard) {
    gameCategoryBadge.textContent = snapshot.currentCard.category;
    gameCardText.textContent = snapshot.currentCard.text;
  }
});

// UI Event Listeners
btnStart.addEventListener("click", () => {
  engine.startCountdown((remainingSeconds: number) => {
    countdownDisplay.textContent = String(remainingSeconds);
  });
});

btnExitGame.addEventListener("click", (e) => {
  e.stopPropagation();
  cancelLongPress();
  engine.exitGame();
});

btnRules.addEventListener("click", () => engine.openRules());
btnCloseRules.addEventListener("click", () => engine.closeRules());

btnPreorderHook.addEventListener("click", () => engine.openInterestModal());
btnClosePreorder.addEventListener("click", () => engine.closeInterestModal());

// Modal Backdrop Dismissal
[modalRules, modalPreorder].forEach((modal) => {
  modal.addEventListener("click", (e) => {
    if (e.target === modal) {
      if (modal === modalRules) engine.closeRules();
      if (modal === modalPreorder) engine.closeInterestModal();
    }
  });
});

// Register Interest Email Submission
btnSubmitPreorder.addEventListener("click", async () => {
  const email = inputEmail.value;
  const result = await engine.registerInterest(email);
  toast.show(result.message);
  if (result.success) {
    inputEmail.value = "";
  }
});

// GPU-Accelerated Pointer-Based Long-Press Interaction
let longPressStartTime: number | null = null;
let animationFrameId: number | null = null;

function startLongPress(e: PointerEvent): void {
  // Ignore clicks on exit button
  if ((e.target as HTMLElement).closest("#btn-exit-game")) return;

  longPressStartTime = performance.now();
  progressFill.classList.remove("active");

  function animate(currentTime: number): void {
    if (longPressStartTime === null) return;
    const elapsed = currentTime - longPressStartTime;
    const progress = Math.min(elapsed / DEFAULT_CONFIG.longPressDurationMs, 1);

    // GPU-accelerated scaleX transform (NO layout reflows)
    progressFill.style.transform = `scaleX(${progress})`;

    if (progress < 1) {
      animationFrameId = requestAnimationFrame(animate);
    } else {
      triggerAdvance();
    }
  }

  animationFrameId = requestAnimationFrame(animate);
}

function cancelLongPress(): void {
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
  longPressStartTime = null;
  progressFill.style.transform = "scaleX(0)";
  progressFill.classList.remove("active");
}

function triggerAdvance(): void {
  cancelLongPress();
  progressFill.classList.add("active");
  progressFill.style.transform = "scaleX(1)";

  engine.advanceCard();

  window.setTimeout(() => {
    progressFill.style.transform = "scaleX(0)";
    progressFill.classList.remove("active");
  }, 180);
}

viewGame.addEventListener("pointerdown", startLongPress);
window.addEventListener("pointerup", cancelLongPress);
window.addEventListener("pointercancel", cancelLongPress);
